/**
 * What the hero says about a cycle the patient closed without naming an ending.
 *
 * `other` is what the end-cycle picker writes when none of the offered endings
 * is the one that happened — and on a cycle that never reached a transfer it is
 * one of only two choices, beside `cancelled`. So it is not an exotic state: it
 * is the ordinary way to close a cycle that simply stopped.
 *
 * Until this existed there was nothing to say it. Closing that way produced no
 * response at all — Home moved on to whatever else it could find, which for a
 * patient with a pregnancy recorded on an older cycle meant being congratulated
 * on a positive result for closing an unrelated cycle. See `latestClosedCycle`
 * for the selection half of that; this is the voice half.
 *
 * The words claim nothing about how the cycle went, because no ending was
 * recorded. That silence is the hard part: this same hero meets a cycle closed
 * for paperwork and a cycle closed because it did not work, and it cannot tell
 * which. So it neither congratulates nor consoles. It says what is true for
 * both — take a breath, nothing is due today, what was recorded is kept, and
 * the ending can be added whenever she wants to.
 *
 * "What was recorded is kept" is load-bearing rather than reassurance-shaped.
 * Closing this way stops the app describing a result as the cycle's ending — see
 * `resolvePostProcedureOutcomeClinicalState` — and a patient who had a beta on
 * file has every reason to read that as her result having been thrown away. It
 * never is: `PATCH /{tenantId}/cycles/{id}` cannot write `resultStatus` at all.
 */

const I18N_ROOT = "homepage.closed_without_a_result";

/**
 * Whether this is that cycle.
 *
 * `status` is read as well as the outcome because the copy states the cycle is
 * closed. `other` is only reachable through a flow that closes it, but a hero
 * that asserts a cycle is over should test that rather than assume it.
 */
export function wasClosedWithoutARecordedEnding(input: {
  cycleStatus?: string | null;
  outcomeType?: string | null;
}): boolean {
  return input.cycleStatus === "closed" && input.outcomeType === "other";
}

export function closedWithoutResultCopyKeys(input: {
  /**
   * A partner reads the same hero. Nothing here is addressed to him — there is
   * no ending to record and no history of his to file it in — so his body says
   * the fact and asks nothing.
   */
  viewerIsSupporting: boolean;
}): { titleKey: string; bodyKey: string } {
  return {
    titleKey: input.viewerIsSupporting
      ? `${I18N_ROOT}.supporting.this_cycle_is_closed`
      : `${I18N_ROOT}.this_cycle_is_closed`,
    bodyKey: input.viewerIsSupporting
      ? `${I18N_ROOT}.supporting.the_record_is_kept_and_nothing_needs_doing`
      : `${I18N_ROOT}.take_a_breath_the_record_is_kept_and_can_wait`,
  };
}
